import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';

const BugsCleanup = ({ selectedProject, authenticatedUser, apiClient }) => {
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  // Filters
  const [duration, setDuration] = useState('1-year'); // Default: 1 year
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [status, setStatus] = useState(''); // Dynamic - will be populated from config/default
  const statusRef = useRef(status);
  const [lastUpdatedEnabled, setLastUpdatedEnabled] = useState(false);
  const [lastUpdatedDuration, setLastUpdatedDuration] = useState('6-months');

  // Keep ref in sync with state
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const loadConfiguration = useCallback(async () => {
    try {
      // TODO: Load from API - for now use defaults
      setStatus(''); // Will be set after statuses are loaded
    } catch (err) {
      console.error('Failed to load configuration:', err);
    }
  }, []);

  const loadStatuses = useCallback(async () => {
    if (!selectedProject?.project_id) return;
    
    try {
      // Calculate date range for current duration
      let startDate = null;
      let endDate = new Date();
      
      if (duration === 'custom') {
        startDate = customStartDate ? new Date(customStartDate) : null;
        endDate = customEndDate ? new Date(customEndDate) : new Date();
      } else {
        const ranges = {
          '6-months': new Date(endDate.getFullYear(), endDate.getMonth() - 6, endDate.getDate()),
          '1-year': new Date(endDate.getFullYear() - 1, endDate.getMonth(), endDate.getDate()),
          '2-years': new Date(endDate.getFullYear() - 2, endDate.getMonth(), endDate.getDate()),
        };
        startDate = ranges[duration] || null;
      }
      
      // Call API to get available statuses from JIRA
      const response = await apiClient.makeRequest('/health-insights/bugs/statuses', {
        method: 'POST',
        body: JSON.stringify({
          project_id: selectedProject.project_id,
          duration: duration,
          custom_start_date: startDate ? startDate.toISOString() : null,
          custom_end_date: endDate ? endDate.toISOString() : null,
        }),
      });
      
      if (response.success && response.data && response.data.length > 0) {
        setStatuses(response.data);
        // Set default status if current status is not in the list
        if (!response.data.includes(statusRef.current)) {
          setStatus(response.data[0] || '');
        }
      } else {
        // Fallback to empty list if no statuses found
        setStatuses([]);
        setStatus('');
      }
    } catch (err) {
      console.error('Failed to load statuses:', err);
      // Fallback to empty list on error
      setStatuses([]);
      setStatus('');
    }
  }, [selectedProject, duration, customStartDate, customEndDate, apiClient]);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration, selectedProject]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const calculateDateRange = (durationValue) => {
    const now = new Date();
    const ranges = {
      '6-months': new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
      '1-year': new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
      '2-years': new Date(now.getFullYear() - 2, now.getMonth(), now.getDate()),
    };
    return ranges[durationValue] || null;
  };

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    setResults([]);

    try {
      // Calculate date ranges
      let startDate = null;
      let endDate = new Date();

      if (duration === 'custom') {
        startDate = customStartDate ? new Date(customStartDate) : null;
        endDate = customEndDate ? new Date(customEndDate) : new Date();
      } else {
        startDate = calculateDateRange(duration);
      }

      if (!startDate) {
        setError('Please select a valid duration or custom date range');
        setLoading(false);
        return;
      }

      // Calculate last updated threshold if enabled
      let lastUpdatedThreshold = null;
      if (lastUpdatedEnabled) {
        lastUpdatedThreshold = calculateDateRange(lastUpdatedDuration);
      }

      // Call API
      const response = await apiClient.makeRequest('/health-insights/bugs/analyze', {
        method: 'POST',
        body: JSON.stringify({
          project_id: selectedProject?.project_id,
          filters: {
            duration: {
              start_date: startDate.toISOString(),
              end_date: endDate.toISOString(),
              type: duration,
            },
            status: status || null,
            last_updated: lastUpdatedEnabled ? {
              threshold: lastUpdatedThreshold?.toISOString(),
              duration: lastUpdatedDuration,
            } : null,
          },
        }),
      });

      if (response.success) {
        setResults(response.data || []);
      } else {
        setError(response.message || 'Failed to analyze bugs');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'Failed to analyze bugs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Bugs Cleanup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Identify bugs that may be obsolete based on current Product Knowledge Base content.
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem' }}>
          Filters
        </Typography>
        <Grid container spacing={3}>
          {/* Duration Filter */}
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Duration</InputLabel>
              <Select
                value={duration}
                label="Duration"
                onChange={(e) => setDuration(e.target.value)}
              >
                <MenuItem value="6-months">6 months</MenuItem>
                <MenuItem value="1-year">1 year</MenuItem>
                <MenuItem value="2-years">2 years</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Custom Date Range */}
          {duration === 'custom' && (
            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          {/* Status Filter */}
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {statuses.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Last Updated Filter */}
          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={lastUpdatedEnabled}
                  onChange={(e) => setLastUpdatedEnabled(e.target.checked)}
                />
              }
              label="Show only bugs not updated in"
            />
            {lastUpdatedEnabled && (
              <FormControl sx={{ ml: 2, minWidth: 200 }} size="small">
                <Select
                  value={lastUpdatedDuration}
                  onChange={(e) => setLastUpdatedDuration(e.target.value)}
                >
                  <MenuItem value="6-months">6 months</MenuItem>
                  <MenuItem value="1-year">1 year</MenuItem>
                  <MenuItem value="2-years">2 years</MenuItem>
                </Select>
              </FormControl>
            )}
          </Grid>

          {/* Action Buttons */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={handleAnalyze}
                disabled={loading}
              >
                Analyze Bugs
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setResults([]);
                  setError(null);
                }}
                disabled={loading}
              >
                Clear Results
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results Table */}
      {results.length > 0 && (
        <Paper sx={{ mt: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Analysis Results ({results.length} bugs)
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Bug ID</TableCell>
                  <TableCell>Match</TableCell>
                  <TableCell>Suggested Status</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Created Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((bug) => (
                  <TableRow key={bug.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {bug.bug_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bug.match_type === 'full' ? 'Full Match' : 'Partial Match'}
                        color={bug.match_type === 'full' ? 'error' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label="Obsolete" color="default" size="small" />
                    </TableCell>
                    <TableCell>
                      {bug.last_updated ? new Date(bug.last_updated).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {bug.created_date ? new Date(bug.created_date).toLocaleDateString() : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Empty State */}
      {!loading && results.length === 0 && !error && (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography>No analysis results yet. Configure filters and click "Analyze Bugs" to get started.</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default BugsCleanup;

